import { Button } from "@talladega/components";
import { CheckCircleIcon, ChevronDownIcon } from "@talladega/icons";
import {
  formatCurrency,
  formatNumber,
  dateTimeFormatter,
} from "@talladega/helpers";
import useCoverageConfig from "@modules/purchase/warranty/hooks/useCoverageConfig";
import {
  ExtendedWarrantyCoverageDetails,
  ExtendedWarrantyRatesResponse,
  ProductCoverage,
} from "@modules/purchase/warranty/types/ExtendedWarrantyRates";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { LocalDate, convert } from "@js-joda/core";
import { ExtendedWarrantyPremiumAutoCanadaDigital } from "@modules/purchase/warranty/types/IAProgram";
import { Disclosure, Transition } from "@headlessui/react";
import { useState } from "react";
import classNames from "classnames";
import useCustomerInfoFromQuery from "@modules/query-string/hooks/useCustomerInfoFromQuery";
import useExtendedWarrantyDefaultRates from "@modules/purchase/warranty/hooks/useExtendedWarrantyDefaultRates";
import useAftermarketPurchaseFromQuery from "@modules/purchase/warranty/hooks/useAftermarketPurchaseFromQuery";
import ProductRateClassEnum from "@common/enums/ProductRateClassEnum";
import { Purchase } from "@modules/purchase/warranty/types/Purchase";

type CoverageSelectionProps = {
  ratesResponse?: ExtendedWarrantyRatesResponse;
  onSelectCoverage: (
    coverage: ProductCoverage,
    selectedCoverageDetails: ExtendedWarrantyCoverageDetails
  ) => void;
  disabled: boolean;
  viewMode?: "quote" | null | undefined;
  rateDiscountValue?: number;
  purchase: Purchase | undefined;
  isPremiumCoverageWithoutSuperiorWrap?: boolean;
};

const WarrantySelectCoverageCards = ({
  ratesResponse,
  onSelectCoverage,
  disabled,
  viewMode,
  rateDiscountValue,
  purchase,
  isPremiumCoverageWithoutSuperiorWrap,
}: CoverageSelectionProps) => {
  const isQuoteMode = viewMode === "quote";
  const { t } = useTranslation(["common", "purchase"]);
  const locale = useRouter().locale || "en";

  const [isOpen, setIsOpen] = useState(false); // state to toggle dropdown visibility
  const coverageConfig = useCoverageConfig(ratesResponse?.coverages, locale);

  /* ================================================================== */
  // Temporary hack to fetch default adjusted rates when displayWarrantySelectCards variation is selected but coverage is Premium (BA/PR/CL/SU).
  // This should all go away once we decide which layout to go with after a/b testing period
  const { province } = useCustomerInfoFromQuery();
  const aftermarketPurchaseFromQuery = useAftermarketPurchaseFromQuery();

  const { vin, mileage, year, make, model, trim, fuelType } =
    aftermarketPurchaseFromQuery;

  const { data: extDefaultRates } = useExtendedWarrantyDefaultRates(
    {
      vin: vin ?? "",
      odometer: mileage as number,
      province: province?.abbr || undefined,
      source: undefined,
      year: year?.toString() ?? "",
      make: make ?? "",
      model: model ?? "",
      trim: trim ?? "",
      fuelType: fuelType ?? "",
      discount: purchase?.appliedPromoCode?.discountAmount,
      productRateClassId: ProductRateClassEnum.ConsumerDefault,
    },
    true,
    locale,
    true
  );
  /* ================================================================== */
  const updatedRatesResponse = isPremiumCoverageWithoutSuperiorWrap
    ? extDefaultRates
    : ratesResponse;

  if (!updatedRatesResponse) return null;

  const isPremiumCoverage =
    updatedRatesResponse.productDetails.programId ===
    ExtendedWarrantyPremiumAutoCanadaDigital.id;

  return (
    <div className="mx-auto flex max-w-xl flex-col-reverse gap-x-4 gap-y-4 text-center 3md:grid 3md:max-w-none 3md:grid-cols-3 3md:justify-center 3md:gap-4 sm:gap-x-6 sm:gap-y-10">
      {updatedRatesResponse.coverages.map((coverage, index) => {
        const config =
          coverageConfig[coverage.type as keyof typeof coverageConfig];
        const isHighlightedCoverage =
          index === updatedRatesResponse.coverages.length - 1;

        return (
          <div
            key={`${coverage.type}-${coverage.term}`}
            className="3md:max-w-[356px] lg:max-w-full"
          >
            <div
              className={classNames(
                "group relative flex flex-col items-center justify-between overflow-hidden rounded-2xl p-4 shadow-elevation-01 transition md:p-6",
                {
                  "border-1 border border-primary-500 bg-primary-700":
                    isHighlightedCoverage,
                  "bg-white text-black": !isHighlightedCoverage,
                }
              )}
            >
              {isHighlightedCoverage && (
                <div className="absolute -right-12 top-7 flex h-8 w-60 rotate-[30deg] items-center justify-center bg-white text-primary-700">
                  <div className="px-12 text-xs font-bold uppercase leading-none">
                    {isPremiumCoverageWithoutSuperiorWrap
                      ? t("purchase:most_coverage")
                      : t("purchase:best_value")}
                  </div>
                </div>
              )}

              <div
                id="coverage-pricing"
                className="flex w-full flex-col px-3 text-left"
              >
                <h3
                  className={classNames("pb-4", {
                    "max-w-[65%] text-white": isHighlightedCoverage,
                  })}
                >
                  {config.type()}
                </h3>
              </div>

              <div className="mt-auto flex w-full flex-col px-3 text-left gap-4">
                {coverage.payment > 0 && (
                  <div className="flex flex-col">
                    <div
                      className={classNames(
                        "flex gap-2 rounded-md bg-gray-25 text-primary",
                        {
                          "text-white bg-primary-700": isHighlightedCoverage,
                        }
                      )}
                    >
                      <div className="flex items-baseline gap-1">
                        <span className="text-center text-4xl font-bold">
                          {formatCurrency(coverage.payment, locale, {
                            showCents: "never",
                          })}
                        </span>
                        <span className="text-md font-semibold">
                          {t("purchase:per_month")}
                        </span>
                      </div>
                      {!!rateDiscountValue && (
                        <div className="flex items-center">
                          <div className="text-success-700 text-xs md:text-sm border border-success-200 rounded-full py-1 px-2 bg-success-50">
                            {`${t("purchase:saved")} ${formatCurrency(
                              rateDiscountValue,
                              locale
                            )}`}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div
                  className={classNames("flex flex-col mb-2", {
                    "text-white": isHighlightedCoverage,
                  })}
                >
                  <p>
                    {isPremiumCoverage
                      ? t("purchase:youre_covered_until")
                      : t("purchase:youre_covered_for")}
                  </p>
                  <p className="font-bold">
                    {config.termDescription(
                      {
                        years: coverage.term / 12,
                        months: coverage.term,
                        kms:
                          coverage.termKm === "999999"
                            ? t("common:unlimited")
                            : formatNumber(
                                parseInt(coverage.termKm ?? "0", 10),
                                locale
                              ),
                        yearString:
                          coverage.term === 12
                            ? t("common:year").toLowerCase()
                            : t("common:years").toLowerCase(),
                        termExpiryDate: coverage.termExpiryDate
                          ? dateTimeFormatter(
                              convert(
                                LocalDate.parse(
                                  coverage.termExpiryDate.toString()
                                )
                              ).toDate(),
                              locale,
                              "MMMM, YYYY"
                            )
                          : undefined,
                      },
                      true
                    )}
                  </p>
                </div>

                {!isQuoteMode && (
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={() =>
                        onSelectCoverage(coverage, updatedRatesResponse)
                      }
                      disabled={disabled}
                      buttonStyle="primary"
                      spacing="tight-hug"
                      fill={isHighlightedCoverage ? "outline" : "filled"}
                    >
                      <span
                        className={`${
                          isHighlightedCoverage ? "text-secondary-700" : ""
                        }`}
                      >
                        {t("common:get_started")}
                      </span>
                    </Button>
                  </div>
                )}
                {config.caption && (
                  <p
                    className={classNames("text-xs font-semibold", {
                      "text-white": isHighlightedCoverage,
                      "text-primary": !isHighlightedCoverage,
                    })}
                  >
                    {config.caption({
                      paymentTerm: coverage.paymentTerm,
                      termKm: `${formatNumber(
                        parseInt(coverage.termKm ?? "", 10),
                        locale
                      )} KM`,
                      deductible: formatCurrency(
                        parseInt(coverage.deductible ?? "", 10),
                        locale,
                        {
                          showCents: "never",
                        }
                      ),
                      downPayment: formatCurrency(
                        parseInt(coverage.downPayment?.toString() ?? "", 10),
                        locale,
                        {
                          showCents: "never",
                        }
                      ),
                      serviceClaimAmount: coverage.serviceClaimAmount,
                      totalClaimAmount: coverage.totalClaimAmount,
                    })}
                  </p>
                )}
                {config.details.length > 0 && (
                  <>
                    <div
                      id="divider-horizontal"
                      className={classNames(
                        "relative border-b border-gray-200 my-3",
                        {
                          "border-primary-500": isHighlightedCoverage,
                        }
                      )}
                    />
                    <Disclosure>
                      {({ open }) => (
                        <div
                          className={classNames("rounded-lg", {
                            "bg-gray-25": !isHighlightedCoverage,
                            "bg-primary-700 text-white": isHighlightedCoverage,
                          })}
                        >
                          <Disclosure.Button
                            className={`flex w-full justify-between rounded-lg text-left focus:outline-none ${
                              open ? "rounded-b-none" : ""
                            }`}
                            onClick={() => setIsOpen(!isOpen)} // toggle dropdown state
                          >
                            <p itemProp="name" className="inline-block">
                              {open
                                ? t("purchase:hide_details")
                                : t("purchase:show_details")}
                            </p>
                            <ChevronDownIcon
                              className={classNames("h-5 w-5", {
                                "rotate-180": open,
                                "text-primary-500":
                                  open && !isHighlightedCoverage,
                                "text-white": isHighlightedCoverage,
                              })}
                            />
                          </Disclosure.Button>

                          <Transition
                            unmount={false}
                            enter="transition duration-100 ease-out"
                            enterFrom="transform scale-95 opacity-0"
                            enterTo="transform scale-100 opacity-100"
                            leave="transition duration-75 ease-out"
                            leaveFrom="transform scale-100 opacity-100"
                            leaveTo="transform scale-95 opacity-0"
                          >
                            <Disclosure.Panel className="focus:outline-none">
                              <div className="flex flex-col gap-2 mt-3">
                                {config.details.map((detail) => (
                                  <div
                                    key={`${detail?.toString()}-${
                                      coverage.type
                                    }`}
                                    className="flex gap-2"
                                  >
                                    <CheckCircleIcon
                                      className={classNames(
                                        "h-5 w-5 flex-shrink-0",
                                        {
                                          "text-secondary":
                                            !isHighlightedCoverage,
                                          "text-white": isHighlightedCoverage,
                                        }
                                      )}
                                    />
                                    <p className="text-sm">{detail}</p>
                                  </div>
                                ))}
                              </div>
                            </Disclosure.Panel>
                          </Transition>
                        </div>
                      )}
                    </Disclosure>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default WarrantySelectCoverageCards;
